/**
 * MainCafeScene — root component cho 3D gameplay.
 *
 * Mount lifecycle:
 *   1. initFromBackend() — load shop + active session (nếu có) qua REST.
 *   2. Nếu chưa có session active + energy > 0 → auto startNewSession() sau 600ms.
 *   3. Customer spawn từ backend (KHÔNG client-side anti-cheat).
 */
import { Suspense, lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useCafe3dStore } from "@/stores/cafe3dStore";

import { BottomNav } from "./components/BottomNav";
import { DialogueBubble } from "./components/DialogueBubble";
import { EnergyEmptyState } from "./components/EnergyEmptyState";
import { HudBar } from "./components/HudBar";
import { CurrentMixAndServe, IngredientBar } from "./components/IngredientBar";
import { ResultOverlay } from "./components/ResultOverlay";
import { RevenueTicker } from "./components/RevenueTicker";

// Lazy-load 3D Canvas (Three.js + R3F + drei ≈ 270 KB gzip). Khi user còn ở
// LoginScreen / IntroScreen / ShopNameScreen sẽ KHÔNG cần payload này.
const CafeCanvas = lazy(() =>
  import("./game3d/CafeCanvas").then((m) => ({ default: m.CafeCanvas })),
);

export function MainCafeScene() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);

  const sceneState = useCafe3dStore((s) => s.sceneState);
  const energy = useCafe3dStore((s) => s.energy);
  const shopName = useCafe3dStore((s) => s.shopName);
  const sessionId = useCafe3dStore((s) => s.sessionId);
  const initFromBackend = useCafe3dStore((s) => s.initFromBackend);
  const startNewSession = useCafe3dStore((s) => s.startNewSession);

  // Init load.
  useEffect(() => {
    let cancelled = false;
    initFromBackend()
      .then(() => {
        if (cancelled) return;
        setInitialized(true);
        // Nếu shop chưa có tên → cần đặt tên trước.
        const name = useCafe3dStore.getState().shopName;
        if (!name) {
          navigate("/onboarding/intro");
        }
      })
      .catch(() => {
        if (!cancelled) navigate("/login");
      });
    return () => {
      cancelled = true;
    };
  }, [initFromBackend, navigate]);

  // Auto-spawn customer khi đã init + idle + còn energy + chưa có session.
  useEffect(() => {
    if (!initialized) return;
    if (sceneState !== "idle") return;
    if (energy <= 0) return;
    if (sessionId !== null) return;
    const t = setTimeout(() => startNewSession(), 600);
    return () => clearTimeout(t);
  }, [initialized, sceneState, energy, sessionId, startNewSession]);

  if (!initialized) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-cream-50 text-coffee-700">
        {t("gameplay.loading_shop")} {shopName ? `“${shopName}”` : ""}…
      </div>
    );
  }

  return (
    <div className="w-full h-full max-w-[480px] mx-auto flex flex-col bg-cream-50 relative overflow-hidden safe-area-top">
      <HudBar />
      <RevenueTicker />

      <div className="flex-1 relative min-h-[320px] bg-sky-200/10">
        <Suspense
          fallback={
            <div className="absolute inset-0 flex items-center justify-center text-coffee-700/60 text-sm">
              {t("gameplay.loading_3d")}
            </div>
          }
        >
          <CafeCanvas />
        </Suspense>
        <DialogueBubble />
        <ResultOverlay />
        <EnergyEmptyState />
      </div>

      <IngredientBar />
      <CurrentMixAndServe />
      <BottomNav />
    </div>
  );
}
